import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button, Card } from '../components/UIComponents';
import { TOKEN_KEYS } from '../api/client';
import { useAuth } from '../hooks/useAuth';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();

  const [error, setError] = useState<string>('');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));

    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const linked = searchParams.get('linked');
    const err = searchParams.get('error') || hashParams.get('error');

    const rawRedirectUrl = searchParams.get('redirect_url') || '/';
    const redirectUrl = rawRedirectUrl.startsWith('/') ? rawRedirectUrl : '/';

    const finalize = async () => {
      // Always clear URL fragment to avoid leaving tokens in history/Referer.
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);

      if (err) {
        setError(err);
        window.setTimeout(() => navigate('/login', { replace: true }), 1500);
        return;
      }

      // Link flow may not include fresh JWTs; refresh user and redirect back.
      if (linked && !accessToken && !refreshToken) {
        await refreshUser();
        navigate(redirectUrl, { replace: true });
        return;
      }

      if (!accessToken || !refreshToken) {
        setError('Missing authentication tokens');
        navigate('/login', { replace: true });
        return;
      }

      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);

      await refreshUser();
      navigate(redirectUrl, { replace: true });
    };

    finalize();
  }, [location.hash, location.search, navigate, refreshUser]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] px-4 bg-slate-50 dark:bg-zinc-950">
      <Card className="p-8 w-full max-w-md">
        {error ? (
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              <div className="mt-4 flex justify-end">
                <Button variant="outline" onClick={() => navigate('/login', { replace: true })}>
                  Go to login
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
            <p className="text-sm text-slate-700 dark:text-zinc-300">Signing you in…</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AuthCallback;

